import _APIInstance from "@/config/configApi";
import _APIInstanceServer from "@/config/configApiServer";
import { API } from "@/constants/common";
import { paramsPowerBi } from "@/models/Params";

export interface IFormatExport {
  format: "png" | "pdf" | "pptx" | "xlsx" | "csv";
}

export default class PowerBiService {
  static getAllDashboards = async (params: paramsPowerBi, lang?: string) => {
    return await _APIInstance
      .get(`${API.POWERBI.GET_ALL_DASHBOARDS}`, {
        params: params,
        headers: {
          lang: lang,
        },
      })
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };
  static getAllDashboardsServer = async (
    params: paramsPowerBi,
    lang?: string
  ) => {
    try {
      const response =  await _APIInstanceServer
      .get(`${API.POWERBI.GET_ALL_DASHBOARDS}`, {
        params: params,
        headers: {
          lang: lang,
        },
      });
      return response?.data;
    } catch (error:any) {
      throw error?.response?.data;
    }
  };

  static getTokenPowerBi = async () => {
    return await _APIInstance
      .get(`${API.POWERBI.GET_POWERBI_TOKEN}`)
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };

  static getFileExportGroup = async (
    workspaceId?: string,
    powerBiId?: string,
    body?: any,
    token?: string
  ) => {
    if (!workspaceId || !powerBiId) {
      return Promise.reject("Missing required parameters");
    }
    const response = await fetch(
      `${API.SERVER.POWER_BI.EXPORT_FILE_IN_GROUP}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ workspaceId, powerBiId, body }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to fetch Power BI reports: ${error.message}`);
    }
    const data = await response.json();
    return data?.value ?? data ?? {};
  };

  static getDashboardByReport = async (reportId?: string | number) => {
    return await _APIInstance
      .get(
        `${API.POWERBI.GET_BY_REPORT}`.replace(
          ":reportId",
          reportId?.toString() || ""
        )
      )
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };
  static getFile = async (
    workspaceId?: string,
    powerBiId?: string,
    exportId?: string | null,
    token?: string
  ) => {
    if (!workspaceId || !powerBiId || !exportId) {
      return Promise.reject(
        "Missing required parameters: workspaceId, powerBiId, or token"
      );
    }
    try {
      const response = await fetch(`${API.SERVER.POWER_BI.GET_FILE}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ workspaceId, powerBiId, exportId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to fetch Power BI reports: ${error.message}`);
      }

      const data = await response.blob();
      return data;
    } catch (error) {
      throw error;
    }
  };
  static getExportStatus = async (
    workspaceId?: string,
    powerBiId?: string,
    exportId?: string,
    token?: string
  ) => {
    if (!workspaceId || !powerBiId || !exportId) {
      return Promise.reject("Missing required parameters");
    }
    const response = await fetch(`${API.SERVER.POWER_BI.GET_FILE_STATUS}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ workspaceId, powerBiId, exportId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to fetch Power BI reports: ${error.message}`);
    }
    const data = await response.json();
    return data?.value ?? data ?? {};
  };

  static getDashboardByToken = async (
    token?: string,
    lang?: string,
    params?: { token: string }
  ) => {
    return await _APIInstance
      .get(
        `${API.POWERBI.GET_ONE}`.replace(":token", token?.toString() || ""),
        {
          params: params,
          headers: {
            lang: lang || "vi",
          },
        }
      )
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };

  static createDashboard = async (data: any) => {
    return await _APIInstance
      .post(`${API.POWERBI.GET_ALL_DASHBOARDS}`, data)
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };

  static deleteDashboard = async (id: string | number | null | undefined) => {
    return await _APIInstance
      .delete(`${API.POWERBI.DELETE}`.replace(":id", id?.toString() || ""))
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };

  static changeNameDashboard = async (
    id: string | number | undefined | null,
    data: FormData
  ) => {
    return await _APIInstance
      .put(
        `${API.POWERBI.CHANGE_NAME}`.replace(":id", id?.toString() || ""),
        data
      )
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };

  static deleteSelectedDashboards = async (data: { bookmarkIds: number[] }) => {
    return await _APIInstance
      .post(`${API.POWERBI.DELETE_SELECTED}`, data)
      .then((res) => Promise.resolve(res.data))
      .catch((err) => Promise.reject(err.response.data));
  };
}
